import axios from 'axios';
import {
  LOGIN_URL,
  REGISTER_URL,
  SEND_VERIFY_URL,
  VERIFY_URL,
  REFRESH_TOKEN_URL,
  SEND_TEXT_URL,
  TEXT_VERIFY_URL,
  FORGOT_PASSWORD_URL,
  PASSWORD_RESET_URL,
  AUTH_CODE_SEND_URL,
  AUTHENTICATE_URL,
  ANON_LOGIN_URL,
  EMAIL_AUTH_CODE_VERIFICATION 
} from './constants';

/**
 * Sends an authentication code.
 *
 * @param {string} email - The user's email address.
 * @param {string} password - The user's password.
 * @param {string} factor - The authentication factor.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postAuthCodeSend = (email, password, factor) =>
  axios.post(
    AUTH_CODE_SEND_URL,
    {
      username: email,
      password,
      factor,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

/**
 * Authenticates a user.
 *
 * @param {string} email - The user's email address.
 * @param {string} password - The user's password.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postAuthenticate = (email, password) =>
  axios.post(
    AUTHENTICATE_URL,
    {
      username: email,
      password,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

/**
 * Logs in a user.
 *
 * @param {string} email - The user's email address.
 * @param {string} password - The user's password.
 * @param {string} factor - The authentication factor.
 * @param {string} code - The authentication code.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const generate2faEmailVerificationCode = (email, password) =>
  axios.post(
    LOGIN_URL,
    {
      username: email,
      password,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

export const postAnonLogin = () => axios.post(ANON_LOGIN_URL);

/**
 * Sends verification code to user's email address.
 *
 * @param {string} email - The user's email address.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postTextSend = email =>
  axios.post(SEND_TEXT_URL, {
    email: email,
  });

/**
 * Verifies code.
 *
 * @param {string} email - The user's email address.
 * @param {string} code - The verification code.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postTextVerify = (email, code) =>
  axios.post(TEXT_VERIFY_URL, {
    email,
    code,
  });


  export const TwoFAEmailVerification = (email, routerPassword, factor, code) =>
    axios.post(EMAIL_AUTH_CODE_VERIFICATION , {
      email,
      code,
    });
/**
 * Registers a new user.
 *
 * @param {string} firstName - The user's first name.
 * @param {string} lastName - The user's last name.
 * @param {string} email - The user's email address.
 * @param {string} password - The user's password.
 * @param {string} phoneNumber - The user's phone number.
 * @param {string|null} registrationCode - The registration code (optional).
 * @param {boolean} emailSubscription - Whether the user is subscribed to email notifications.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postRegister = (
  firstName,
  lastName,
  email,
  password,
  phoneNumber,
  registrationCode = null,
  emailSubscription,
  token,
) =>
  axios.post(REGISTER_URL, {
    first_name: firstName,
    last_name: lastName,
    email,
    password,
    phone_number: phoneNumber,
    registration_code: registrationCode,
    is_email_subscribed: emailSubscription,
    anon_token: token,
  });

/**
 * Sends a verification email.
 *
 * @param {string} email - The user's email address.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postSendVerifyEmail = email =>
  axios.post(SEND_VERIFY_URL, {
    email,
  });

/**
 * Verifies an email address.
 *
 * @param {string} email - The user's email address.
 * @param {string} code - The verification code.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postVerify = (email, code) =>
  axios.post(VERIFY_URL, {
    email,
    code,
  });

/**
 * Refreshes the authentication token.
 *
 * @param {string} refreshToken - The refresh token.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postRefreshToken = refreshToken =>
  axios.post(REFRESH_TOKEN_URL, {
    refresh_token: refreshToken,
  });

/**
 * Sends a request to reset the user's password.
 *
 * @param {string} email - The user's email address.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postForgotPassword = email =>
  axios.post(FORGOT_PASSWORD_URL, null, {
    params: {
      email,
    },
  });

/**
 * Resets the user's password.
 *
 * @param {string} token - The password reset token.
 * @param {string} password - The new password.
 * @returns {Promise} A Promise that resolves when the request is complete.
 */
export const postPasswordReset = (token, password) =>
  axios.post(`${PASSWORD_RESET_URL}/${token}`, null, {
    params: {
      password,
    },
  });

/**
 * Gets the validity of a password reset token.
 *
 * @param {string} token - The password reset token.
 * @returns {Promise} A Promise that resolves with the token validity information.
 */
export const getPasswordResetValidity = token => axios.get(`${PASSWORD_RESET_URL}/${token}`);
